import CtaIcon from '../ui/CtaIcon';
import { cardRecordState } from '@bilt/domain/cardRecord';
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  cardRequest,
  cardSubjectPath,
  type CardHub,
  type PassportCard as Card,
} from '../../services/api/passportCards';
import { hasNativeCapability, openNativeScanner } from '../../utils/nativeShell';
import PassportCard from './PassportCard';
import { PassportProgramShelf, PassportVisitHistory } from './PassportPrograms';
import PassportPage from './PassportPage';
import './passport-cabinet.css';
import './passport-cinematic.css';
import CollectorCard from './CollectorCard';
import CollectorCardSkeleton from './CollectorCardSkeleton';
export function PassportScanButton() {
  const [help, setHelp] = useState(false);
  const { user, isLoading } = useAuth();
  if (!user || isLoading) return null;
  return (
    <>
      <button
        className="pc-button"
        onClick={() =>
          hasNativeCapability('qr_scan') ? openNativeScanner('passport') : setHelp(!help)
        }
      >
        Scan a QR{' '}
        <span aria-hidden="true">
          <CtaIcon direction="open" />
        </span>
      </button>
      {help && (
        <p className="pc-muted" role="status">
          Open your iPhone camera and scan a Bilt QR. Follow the link to collect it in Passport. The
          Bilt app also has a scanner.
        </p>
      )}
    </>
  );
}
export function PassportNav({ active }: { active: string }) {
  // Passport is now a single overview surface. Keep this export as a no-op
  // for deep pages that still import it without reintroducing the old tab row.
  void active;
  return null;
}
export default function PassportHub({
  view = 'overview',
}: {
  view?: 'overview' | 'collection' | 'explore';
}) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading)
    return (
      <div className="pc-page" role="status" aria-busy="true">
        <span className="sr-only">Opening Passport…</span>
        <div className="pc-grid">{[0, 1, 2].map((i) => <CollectorCardSkeleton key={i} />)}</div>
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <HubContent key={`${user.id}:${view}`} view={view} />;
}
function HubContent({ view }: { view: string }) {
  const [hub, setHub] = useState<CardHub | null>(null);
  const scrollRoot = useRef<HTMLDivElement>(null);
  const shelf = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const dialogTitle = useRef<HTMLHeadingElement>(null);
  const [arrival, setArrival] = useState<string | null>(null);
  const [explore, setExplore] = useState<Card[]>([]);
  const [error, setError] = useState('');
  const [paused, setPaused] = useState(false);
  const [params, setParams] = useSearchParams();
  const filter = params.get('type') || '';
  const sort = params.get('sort') || 'newest';
  const compact = params.get('layout') === 'list';
  const [search, setSearch] = useState(params.get('q') || '');
  const query = params.get('q') || '';
  const collected = params.get('collected');
  useEffect(() => {
    const card = hub?.cards.find(
      (c) =>
        c.key === collected &&
        c.ownership_status === 'active' &&
        c.earned_at &&
        c.evidence !== 'offline_pending'
    );
    if (!card?.key) return;
    const marker = `bilt:arrival:${card.key}:${card.earned_at}`;
    try {
      if (sessionStorage.getItem(marker)) return;
      sessionStorage.setItem(marker, 'seen');
    } catch {
      /* Private browsing can disable storage. */
    }
    setArrival(card.key);
  }, [hub, collected]);
  function changeParam(key: string, value: string) {
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  }
  useEffect(() => {
    if (search === query) return;
    const timer = setTimeout(() => {
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (search) next.set('q', search);
          else next.delete('q');
          return next;
        },
        { replace: true }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [search, query, setParams]);
  const [selected, setSelected] = useState<Card | null>(null);
  useEffect(() => {
    if (!selected?.key || !selected.earned_at) return;
    let active = true;
    void cardRequest<Card>('card', undefined, { key: selected.key })
      .then((card) => {
        if (active) setSelected(card);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
    // Fetch inspector detail only when its stable selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.key]);
  const [back, setBack] = useState(false);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    let active = true;
    setError('');
    setHub(null);
    void Promise.all([
      cardRequest<CardHub>('hub', undefined, { type: filter, q: query, sort }),
      cardRequest<Card[]>('explore'),
    ])
      .then(([h, e]) => {
        if (active) {
          setHub(h);
          setExplore(e);
        }
      })
      .catch((e) => {
        if (active) {
          setError(e.message);
          if (e.name === 'PassportCardsPaused' || e.code === 'cards_unavailable') setPaused(true);
        }
      });
    return () => {
      active = false;
    };
  }, [filter, query, sort, revision]);
  useEffect(() => {
    if (selected) {
      opener.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setBack(false);
      dialog.current?.showModal();
      dialogTitle.current?.focus({ preventScroll: true });
    }
  }, [selected]);
  async function loadMore() {
    if (hub?.next_offset == null) return;
    setLoading(true);
    try {
      const next = await cardRequest<CardHub>('hub', undefined, {
        offset: String(hub.next_offset),
        type: filter,
        q: query,
        sort,
      });
      setHub({ ...next, cards: [...hub.cards, ...next.cards] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  async function showcase(card: Card) {
    if (!hub || !card.key) return;
    try {
      const old = hub.preferences.showcase;
      const next = old.includes(card.key)
        ? old.filter((k) => k !== card.key)
        : [...old, card.key].slice(-3);
      const preferences = await cardRequest<CardHub['preferences']>('preferences', {
        showcase: next,
      });
      setHub({ ...hub, preferences });
      setSelected(null);
      dialog.current?.close();
      setRevision((r) => r + 1);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const next = explore.find((c) => !c.owned);
  const nextSet = hub?.next_set;
  const cards =
    view === 'explore'
      ? explore.filter(
          (c) =>
            !query ||
            `${c.title} ${c.issuer} ${c.rules.venue_name || ''}`
              .toLocaleLowerCase()
              .includes(query.toLocaleLowerCase())
        )
      : hub?.cards || [];
  const path = selected ? cardSubjectPath(selected) : null;
  if (paused) return <PassportPage />;
  return (
    <div
      className="pc-page"
      ref={scrollRoot}
      style={{ '--pc-material': 'url(/images/passport/card-material.jpg)' } as React.CSSProperties}
    >
      <div className="pc-toolbar">
        <Link to="/passport" className="pc-eyebrow">
          Bilt Passport / Your collection
        </Link>
        <div className="pc-actions">
          <PassportScanButton />
        </div>
      </div>
      <PassportNav active={view} />
      {error && (
        <div className="pc-error" role="alert">
          {error}{' '}
          <button className="pc-link" onClick={() => setRevision((r) => r + 1)}>
            Try again
          </button>
        </div>
      )}
      {!hub && !error && (
        <div className="pc-grid" role="status" aria-busy="true">
          <span className="sr-only">Opening your collection…</span>
          {['vehicle', 'event', 'place', 'club'].map((kind) => <CollectorCardSkeleton key={kind} kind={kind} />)}
        </div>
      )}
      {hub && (
        <>
          <div className="pc-intro">
            <span className="pc-eyebrow">
              {view === 'overview'
                ? 'Keep the good finds.'
                : view === 'explore'
                  ? 'Your next discovery'
                  : 'A collection with a story'}
            </span>
            <h1>
              {view === 'overview'
                ? 'Look what you found.'
                : view === 'explore'
                  ? 'Worth pulling over for.'
                  : 'Your collection.'}
            </h1>
            <p>
              {view === 'overview'
                ? 'The builds that stopped you. The places you found your people. All here, one scan at a time.'
                : view === 'explore'
                  ? 'Discover available cards and the hosts behind them. Check the collection rules before you go.'
                  : 'Every card leads back to a car, a place, or a moment. Pick one up to see its story.'}
            </p>
          </div>
          {view === 'explore' && <PassportProgramShelf />}
          {view === 'overview' && (
            <div className="pc-showcase">
              <div>
                <div className="pc-stats">
                  {[
                    ['vehicle', 'Cars collected'],
                    ['event', 'Events collected'],
                    ['place', 'Places collected'],
                    ['editions', 'Editions earned'],
                    ['visits', 'Recorded visits'],
                    ['ready_rewards', 'Rewards ready'],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <strong>{hub.stats[key] || 0}</strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="pc-next">
                  <span className="pc-eyebrow">
                    {nextSet ? 'Your next set' : next ? 'Next up' : 'A good place to start'}
                  </span>
                  <h2>
                    {nextSet?.title ||
                      next?.title ||
                      (hub.cards.length
                        ? 'Find a story you haven’t seen.'
                        : 'Your first card is out there.')}
                  </h2>
                  <p>
                    {nextSet
                      ? `${Math.round(nextSet.percent_complete)}% complete. ${nextSet.missing} requirement${nextSet.missing === 1 ? '' : 's'} left. Continue the set you’ve already started.`
                      : next
                        ? `${next.issuer}. ${next.rules.evidence === 'location' ? 'Collect at the venue with location enabled.' : next.rules.evidence === 'staff' ? 'Ask the host to confirm your visit.' : 'Find the host’s Bilt QR to collect.'}`
                        : 'Look for a Bilt QR on a build or at a meet. Scan it, discover the story, and keep the card.'}
                  </p>
                  {nextSet ? (
                    <Link
                      className="pc-link"
                      to={`/passport/collections?set=${encodeURIComponent(nextSet.slug)}`}
                    >
                      See what’s missing <CtaIcon direction="open" />
                    </Link>
                  ) : next ? (
                    <button className="pc-link" onClick={() => setSelected(next)}>
                      See this card <CtaIcon direction="open" />
                    </button>
                  ) : (
                    <Link className="pc-link" to="/events">
                      Find an event <CtaIcon direction="open" />
                    </Link>
                  )}
                </div>
              </div>
              <div className="pc-showcase-cards" aria-label="Your showcase">
                {hub.showcase.length ? (
                  hub.showcase.map((card) => (
                    <CollectorCard
                      key={card.key}
                      className="pc-showcase-card"
                      card={card}
                      onOpen={() => setSelected(card)}
                      openLabel={`Inspect ${card.title}`}
                    />
                  ))
                ) : (
                  <div className="pc-empty">
                    <div className="pc-eyebrow">Reserved for your first find</div>
                    <h2>
                      A little empty.
                      <br />
                      For now.
                    </h2>
                    <p className="pc-muted">
                      No car ownership required. Your collection starts with curiosity.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <section className="pc-section">
            <div className="pc-toolbar">
              <h2>
                {view === 'overview'
                  ? 'Fresh in your Passport'
                  : view === 'explore'
                    ? 'Available to discover'
                    : 'Collected and kept'}
              </h2>
              {view !== 'explore' && (
                <label className="pc-muted">
                  Show{' '}
                  <select
                    className="pc-filter"
                    value={filter}
                    onChange={(e) => changeParam('type', e.target.value)}
                    aria-label="Filter your cards"
                  >
                    <option value="">Everything</option>
                    <option value="vehicle">Cars</option>
                    <option value="event">Events</option>
                    <option value="club">Clubs</option>
                    <option value="shop">Shops</option>
                    <option value="place">Places</option>
                    <option value="commemorative">Commemoratives</option>
                    <option value="checkpoint">Checkpoints</option>
                    <option value="edition">Editions</option>
                  </select>
                </label>
              )}
            </div>
            {view === 'explore' && (
              <label className="pc-muted pc-search">
                Find a host or venue{' '}
                <input
                  className="pc-filter"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="City, venue, club or card"
                />
              </label>
            )}
            {view === 'collection' && (
              <div className="pc-actions pc-search">
                <label className="pc-muted">
                  Find a card{' '}
                  <input
                    className="pc-filter"
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Car, event, place or edition"
                  />
                </label>
                <label className="pc-muted">
                  Sort{' '}
                  <select
                    className="pc-filter"
                    value={sort}
                    onChange={(e) => changeParam('sort', e.target.value)}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">First finds</option>
                  </select>
                </label>
                {view === 'collection' && (
                  <label>
                    Display{' '}
                    <select
                      className="pc-filter"
                      value={compact ? 'list' : 'grid'}
                      onChange={(e) => changeParam('layout', e.target.value)}
                    >
                      <option value="grid">Card grid</option>
                      <option value="list">Compact list</option>
                    </select>
                  </label>
                )}
              </div>
            )}
            {cards.length ? (
              <>
                {view === 'overview' && cards.length > 1 && (
                  <div className="pc-shelf-controls" aria-label="Collection shelf controls">
                    <button
                      className="pc-link"
                      aria-label="Previous cards"
                      onClick={() =>
                        shelf.current?.scrollBy({
                          left: -300,
                          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
                            ? 'auto'
                            : 'smooth',
                        })
                      }
                    >
                      <CtaIcon direction="back" /> Previous
                    </button>
                    <button
                      className="pc-link"
                      aria-label="Next cards"
                      onClick={() =>
                        shelf.current?.scrollBy({
                          left: 300,
                          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
                            ? 'auto'
                            : 'smooth',
                        })
                      }
                    >
                      Next <CtaIcon direction="next" />
                    </button>
                  </div>
                )}
                <div
                  className={
                    compact && view === 'collection'
                      ? 'pe-collection-list'
                      : `pc-grid ${view === 'overview' ? 'pc-shelf' : ''}`
                  }
                  ref={shelf}
                >
                  {(view === 'overview' ? cards.slice(0, 4) : cards).map((card) => (
                    <CollectorCard
                      key={card.key || card.id}
                      card={card}
                      onOpen={() => setSelected(card)}
                      openLabel={`Inspect ${card.title}`}
                      className={compact && view === 'collection' ? 'collector-card--list' : ''}
                      arrival={arrival === card.key}
                      quickReveal={hub.preferences.quick_reveal}
                      fullRecordExtension={
                        <>
                          {compact && view === 'collection' && (
                            <p className="text-sm text-zinc-400">
                              {card.subject_type} · {cardRecordState(card)}
                              {card.earned_at ? ` · ${card.earned_at.slice(0, 10)}` : ''}
                            </p>
                          )}
                        </>
                      }
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="pc-empty">
                <h2>
                  {view === 'explore'
                    ? query
                      ? 'No available cards match this place yet.'
                      : 'The next drop is still taking shape.'
                    : filter || query
                      ? 'No matching cards.'
                      : 'Something worth finding.'}
                </h2>
                <p className="pc-muted">
                  {view === 'explore'
                    ? 'There are no available editions to show right now. Browse upcoming events, public places or builds for your next outing.'
                    : 'Scan a Bilt QR to add your first card. Existing photo memories live in your private archive.'}
                </p>
                <div className="pc-actions">
                  <Link className="pc-link" to="/events">
                    Browse events
                  </Link>
                  <Link className="pc-link" to="/discover">
                    Explore builds
                  </Link>
                </div>
              </div>
            )}
            {view === 'overview' && cards.length > 0 && (
              <Link className="pc-link" to="/passport">
                Open your Passport <CtaIcon direction="open" />
              </Link>
            )}
            {view === 'collection' && hub.next_offset != null && (
              <button className="pc-link" disabled={loading} onClick={loadMore}>
                {loading ? 'Loading…' : 'Load more cards'}
              </button>
            )}
          </section>
          {view === 'overview' && !!hub.recent?.length && (
            <section className="pc-section">
              <div className="pc-toolbar">
                <h2>Your recent story</h2>
              </div>
              {hub.recent.map((c) => (
                <button
                  className="pc-activity pc-card-button"
                  key={c.key}
                  onClick={() => setSelected(c)}
                >
                  <span>{c.title}</span>
                  <span className="pc-muted">
                    {cardRecordState(c)} · {c.earned_at?.slice(0, 10)}
                  </span>
                </button>
              ))}
            </section>
          )}
          {view === 'overview' && <PassportProgramShelf compact />}
          <section className="pc-section">
            <div className="pc-toolbar">
              <div>
                <h2>Make a day of it.</h2>
                <p className="pc-muted">
                  Find sets to complete, community hunts, and something to come back for.
                </p>
              </div>
              <div className="pc-actions">
                <Link className="pc-link" to="/passport/collections">
                  Official sets
                </Link>
                <Link className="pc-link" to="/passport/campaigns">
                  Community hunts
                </Link>
              </div>
            </div>
          </section>
          <footer className="pc-toolbar pc-section">
            <div className="pc-actions">
              <Link className="pc-link" to="/passport/archive">
                Photo archive
              </Link>
              <Link className="pc-link" to="/passport/settings">
                Privacy
              </Link>
              <Link className="pc-link" to="/passport/preferences">
                Your interests
              </Link>
              <label className="pc-muted">
                <input
                  type="checkbox"
                  checked={hub.preferences.quick_reveal}
                  onChange={async (e) => {
                    try {
                      const preferences = await cardRequest<CardHub['preferences']>('preferences', {
                        quick_reveal: e.target.checked,
                      });
                      setHub({ ...hub, preferences });
                    } catch (err) {
                      setError((err as Error).message);
                    }
                  }}
                />{' '}
                Quick reveals in the app
              </label>
              <label className="pc-muted">
                <input
                  type="checkbox"
                  checked={hub.preferences.haptics_enabled !== false}
                  onChange={async (e) => {
                    try {
                      const preferences = await cardRequest<CardHub['preferences']>('preferences', {
                        haptics_enabled: e.target.checked,
                      });
                      setHub({ ...hub, preferences });
                    } catch (err) {
                      setError((err as Error).message);
                    }
                  }}
                />{' '}
                Haptics in the app
              </label>
            </div>
          </footer>
        </>
      )}
      <dialog
        ref={dialog}
        className="pc-dialog"
        onClose={() => {
          setSelected(null);
          opener.current?.focus({ preventScroll: true });
        }}
        aria-label={selected?.title || 'Card details'}
      >
        <div className="pc-toolbar">
          <span className="pc-eyebrow">
            {selected?.earned_at ? 'Your Passport' : 'Card preview'}
          </span>
          <div className="pc-actions">
            {selected && (
              <button className="pc-link" aria-pressed={back} onClick={() => setBack(!back)}>
                {back ? 'See the front' : 'Turn it over'}
              </button>
            )}
            <button className="pc-link" onClick={() => dialog.current?.close()}>
              Close
            </button>
          </div>
        </div>
        {selected && (
          <div className="pc-dialog-grid" data-side={back ? 'back' : 'front'}>
            <PassportCard
              key={selected.key || selected.id || selected.title}
              card={selected}
              back={back}
              onFlip={() => setBack((value) => !value)}
              readableStory
            />
            <div className="pc-dialog-copy">
              <h2 ref={dialogTitle} tabIndex={-1}>
                {selected.title}
              </h2>
              <p>
                {selected.earned_at
                  ? `Recorded ${new Date(selected.earned_at.replace(' ', 'T') + (selected.earned_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString()}.`
                  : selected.rules.evidence === 'location'
                    ? 'Scan the host’s QR at the venue. Bilt checks a fresh location reading when you collect.'
                    : selected.rules.evidence === 'staff'
                      ? 'Ask the host to confirm your visit and issue your card.'
                      : 'Scan the host’s Bilt QR to collect this card.'}
              </p>
              {selected.earned_at && <p className="pc-collection-evidence">{cardRecordState(selected)}</p>}
              {!!selected.editions?.length && (
                <section>
                  <h3>Editions of this subject</h3>
                  {selected.editions.map((e) => (
                    <button key={e.key} className="pc-link" onClick={() => setSelected(e)}>
                      {e.title}
                      {e.ordinal ? ` · No. ${e.ordinal}` : ''}
                    </button>
                  ))}
                  {selected.issuance_id && selected.base_key && (
                    <button
                      className="pc-link"
                      onClick={() =>
                        void cardRequest<Card>('card', undefined, { key: selected.base_key! })
                          .then(setSelected)
                          .catch((e) => setError(e.message))
                      }
                    >
                      View base collectible
                    </button>
                  )}
                </section>
              )}
              {selected.earned_at && selected.key && (
                <Link
                  className="pc-link"
                  to={`/passport/export-card?key=${encodeURIComponent(selected.key)}`}
                >
                  Export both faces
                </Link>
              )}
              {selected.earned_snapshot && (
                <details>
                  <summary>Facts when this visit was recorded</summary>
                  <p>
                    {selected.earned_snapshot.title} · Captured{' '}
                    {selected.earned_snapshot.captured_at}
                  </p>
                  <dl>
                    {Object.entries(selected.earned_snapshot.facts || {}).map(([k, v]) => (
                      <div key={k}>
                        <dt>{k.replaceAll('_', ' ')}</dt>
                        <dd>{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
              {selected.rules.cap && (
                <p>
                  {selected.ordinal
                    ? `Your card is number ${selected.ordinal} in an edition of ${selected.rules.cap}.`
                    : `An edition of ${selected.rules.cap}. ${selected.remaining ?? 0} available when this page loaded.`}
                </p>
              )}
              {!selected.subject && (
                <p>
                  The original subject is no longer public. Your collection record is kept private.
                </p>
              )}
              {back && (
                <div className="pc-inspector-story">
                  <span className="pc-eyebrow">Behind the card</span>
                  <p>
                    {selected.design.story ||
                      'A car, a place, a moment. Kept in your Bilt Passport.'}
                  </p>
                </div>
              )}
              {!selected.earned_at && selected.rules.venue_name && (
                <p className="pc-muted">
                  Collect at {selected.rules.venue_name}.{' '}
                  <a
                    className="pc-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.rules.venue_name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See the venue <CtaIcon direction="open" />
                  </a>
                </p>
              )}
              {selected.subject?.subject_ref && (
                <PassportVisitHistory subjectRef={selected.subject.subject_ref} />
              )}
              {selected.issuance_id && (
                <details>
                  <summary className="pc-link">Card receipt</summary>
                  <p className="pc-muted">{selected.issuance_id}</p>
                </details>
              )}
              <div className="pc-actions">
                {selected.key && selected.ownership_status === 'active' && (
                  <button className="pc-link" onClick={() => showcase(selected)}>
                    {hub?.preferences.showcase.includes(selected.key)
                      ? 'Remove from showcase'
                      : 'Add to showcase'}
                  </button>
                )}
                {path && (
                  <Link className="pc-button secondary" to={path}>
                    Explore the story <CtaIcon direction="open" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
}
